import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { authAPI } from '@/services/api';
import { Lock, Eye, EyeOff, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import AuthLayout, { themes } from '@/components/layout/AuthLayout';
import { useTheme } from '@/hooks/useTheme';

function useFormStyles() {
  const { theme } = useTheme();
  const t = themes[theme] || themes.dark;
  return {
    input: {
      width: '100%', padding: '13px 16px', borderRadius: '12px', border: `1.5px solid ${t.inputBorder}`,
      background: t.inputBg, color: t.text, fontSize: '14px', fontFamily: "'Inter',sans-serif",
      transition: 'all .25s', outline: 'none',
    },
    inputFocus: {
      borderColor: t.inputFocus, background: t.surface2, boxShadow: `0 0 0 3px ${t.surface}`,
    },
    label: {
      display: 'block', fontSize: '13px', fontWeight: 600, color: t.text2, marginBottom: '6px',
    },
    btnPrimary: {
      background: t.gradBtn, color: t.bg, fontWeight: 600, position: 'relative', overflow: 'hidden',
      transition: 'all .3s cubic-bezier(.16,1,.3,1)', cursor: 'pointer', border: 'none',
      fontFamily: "'Inter',sans-serif",
    },
    t,
  };
}

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const styles = useFormStyles();
  const { t } = styles;

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => navigate('/login'), 3000);
      return () => clearTimeout(timer);
    }
  }, [success, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword(token, { password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  const rightPanel = success ? (
    <div className="text-center" style={{ animation: 'fadeUp .7s .2s cubic-bezier(.16,1,.3,1) forwards', opacity: 0 }}>
      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 border-2" style={{ background: t.surface2, borderColor: t.success }}>
        <CheckCircle className="w-7 h-7" style={{ color: t.success }} />
      </div>
      <h3 className="text-lg font-bold mb-2" style={{ color: t.text }}>Password updated</h3>
      <p className="text-sm mb-6" style={{ color: t.text3 }}>Your password has been reset. Redirecting to login…</p>
      <Link to="/login" className="btn-p inline-flex items-center gap-2 px-8 py-3 rounded-xl text-sm" style={styles.btnPrimary}>
        <ArrowLeft className="w-4 h-4" /> Back to login
      </Link>
    </div>
  ) : (
    <>
      <div className="mb-8" style={{ animation: 'fadeUp .7s .2s cubic-bezier(.16,1,.3,1) forwards', opacity: 0 }}>
        <h2 className="text-2xl font-bold mb-1.5" style={{ color: t.text }}>Set new password</h2>
        <p className="text-sm" style={{ color: t.text3 }}>Enter your new password below.</p>
      </div>

      <form onSubmit={handleSubmit} noValidate style={{ animation: 'fadeUp .7s .3s cubic-bezier(.16,1,.3,1) forwards', opacity: 0 }}>
        {/* Password */}
        <div className="mb-4">
          <label className="form-label" style={styles.label}>New password</label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: t.text4 }} />
            <input
              type={showPass ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
              className="form-input" style={{ ...styles.input, paddingLeft: '40px', paddingRight: '44px' }}
              placeholder="Create a new password" autoComplete="new-password"
              onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
              onBlur={(e) => { e.target.style.borderColor = error ? t.error : t.inputBorder; e.target.style.background = t.inputBg; e.target.style.boxShadow = 'none'; }}
            />
            <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 p-1 transition-colors" style={{ color: t.text4, background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setShowPass(!showPass)} onMouseEnter={(e) => e.currentTarget.style.color = t.text2} onMouseLeave={(e) => e.currentTarget.style.color = t.text4}>
              {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Confirm */}
        <div className="mb-4">
          <label className="form-label" style={styles.label}>Confirm password</label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: t.text4 }} />
            <input
              type={showConfirm ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              className="form-input" style={{ ...styles.input, paddingLeft: '40px', paddingRight: '44px' }}
              placeholder="Repeat your password" autoComplete="new-password"
              onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
              onBlur={(e) => { e.target.style.borderColor = error ? t.error : t.inputBorder; e.target.style.background = t.inputBg; e.target.style.boxShadow = 'none'; }}
            />
            <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 p-1 transition-colors" style={{ color: t.text4, background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setShowConfirm(!showConfirm)} onMouseEnter={(e) => e.currentTarget.style.color = t.text2} onMouseLeave={(e) => e.currentTarget.style.color = t.text4}>
              {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {error && <div className="flex items-center gap-1 mb-4 text-xs" style={{ color: t.error }}><span>!</span><span>{error}</span></div>}

        <button type="submit" disabled={loading} className="btn-p w-full py-3.5 rounded-xl text-sm flex items-center justify-center" style={styles.btnPrimary}>
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Updating…</> : 'Reset password'}
        </button>
      </form>

      <p className="text-center text-sm mt-8" style={{ color: t.text3, animation: 'fadeUp .7s .4s cubic-bezier(.16,1,.3,1) forwards', opacity: 0 }}>
        <Link to="/login" className="font-semibold hover:opacity-70 transition-opacity inline-flex items-center gap-1" style={{ color: t.text, textDecoration: 'none' }}>
          <ArrowLeft className="w-3.5 h-3.5" /> Back to login
        </Link>
      </p>
    </>
  );

  return <AuthLayout rightPanel={rightPanel} />;
}
